import { deliveryHours, RecognitionScheduleDraftSchema, type RecognitionScheduleDraft, type RecognitionScheduleEntry, type RecognitionScheduleWorkspace } from "@chro/shared";

export type ScheduleItem = RecognitionScheduleWorkspace["items"][number];
export type ScheduleAllocation = RecognitionScheduleDraft["allocations"][number];

/** Calendar choices only. The server owns every monetary preview and approval. */
export function quarterAllocations(year: number, quarter: number, byHours: boolean): ScheduleAllocation[] {
  if (!Number.isInteger(year) || year < 2000 || year > 2100 || !Number.isInteger(quarter) || quarter < 1 || quarter > 4) return [];
  const first = (quarter - 1) * 3;
  const weights = Array.from({ length: 3 }, (_, i) => byHours
    ? deliveryHours(Date.UTC(year, first + i, 1), Date.UTC(year, first + i + 1, 0)) : 1);
  const total = weights.reduce((sum, n) => sum + n, 0);
  let allocated = 0;
  return weights.map((weight, i) => {
    const percent = i === 2 ? 100 - allocated : Math.round(weight / total * 100_000_000) / 1_000_000;
    allocated += percent;
    return { month: `${year}-${String(first + i + 1).padStart(2, "0")}`, percent };
  });
}

export function itemTradeIds(item: ScheduleItem): string[] {
  return [...new Set(item.sourceTradeIds)];
}

export function draftForEntry(entry: RecognitionScheduleEntry): RecognitionScheduleDraft {
  return RecognitionScheduleDraftSchema.parse({ itemId: entry.itemId, sourceFingerprint: entry.sourceFingerprint,
    quantity: entry.quantity, allocations: entry.allocations, reason: entry.reason, replacesId: entry.replacesId });
}

export function scheduleItemQuantityUnit(item: ScheduleItem): string {
  // EEX power and supported continental gas sources use MW contract quantity;
  // do not relabel the differently sized NBP contracts or an unknown unit.
  return item.source.unit === "MWh" && !/^G9[BF]/i.test(item.source.contract.code) ? "MW" : "source units";
}

export function scheduleDraftError(item: ScheduleItem, draft: unknown): string | null {
  const parsed = RecognitionScheduleDraftSchema.safeParse(draft);
  if (!parsed.success) {
    const issue = parsed.error.issues[0];
    if (issue?.path[0] === "reason") return "Enter a reason with at least 3 characters.";
    if (issue?.path[0] === "quantity") return "Enter a positive source quantity with no more than six decimal places.";
    if (issue?.path[0] === "allocations" && issue.code !== "custom") return "Choose valid months and positive allocation percentages.";
    return issue?.message ?? "Check the schedule fields.";
  }
  if (parsed.data.quantity > item.quantity) return `Quantity cannot exceed ${item.quantity} ${scheduleItemQuantityUnit(item)}.`;
  return null;
}

export function scheduleMoney(value: number | null, currency = "EUR"): string {
  return value == null ? "Unavailable" : `${new Intl.NumberFormat("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value)} ${currency}`;
}
